import { useState, useEffect, useCallback, useMemo } from 'react';
import { getTransactionReport, getBankTransactions, getTransactionCategories, downloadReportExport, getReportSavedViews, createReportSavedView, deleteReportSavedView } from '@/lib/bankTransactionsApi';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, ChevronRight, Printer, Filter, History, EyeOff, Eye, FileDown, FileText, AlertTriangle, Bookmark, Save, Trash2 } from 'lucide-react';
import TransactionEditDialog from '@/components/TransactionEditDialog';
import { toast } from 'sonner';

const fmt = (val) => {
  if (val == null || isNaN(val)) return '$0.00';
  const negative = val < 0;
  const str = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(Math.abs(val));
  return negative ? `-${str}` : str;
};

const formatDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  const months = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[m]} ${day}, ${y}`;
};

const formatDateShort = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return `${m}/${day}/${y}`;
};

/* Clickable transaction row — opens edit modal on click */
function TxnRow({ txn, onEdit, size = 'sm' }) {
  const display = txn.title || txn.description;
  const history = txn.edit_history || [];
  const hasDateEdits = history.some(h => h.field === 'date');
  const hasAmountEdits = history.some(h => h.field === 'amount');

  const sizes = {
    sm: { row: 'py-0.5 text-[10px] text-[#a8b5c2]', date: 'w-14' },
    md: { row: 'py-0.5 text-[11px] text-[#8a9bae]', date: 'w-14' },
    lg: { row: 'py-1 text-[12px] text-[#5b6773]', date: 'w-16' },
  };
  const s = sizes[size] || sizes.sm;

  return (
    <button
      onClick={() => onEdit(txn)}
      className={`w-full flex items-center justify-between px-1 rounded-sm hover:bg-[#f5f0eb]/60 transition-colors ${s.row}`}
      data-testid="report-transaction-row"
    >
      <span className={`${s.date} tabular-nums flex-shrink-0 text-left inline-flex items-center gap-0.5`}>
        {formatDateShort(txn.date)}
        {hasDateEdits && <History className="h-2.5 w-2.5 text-amber-600" data-testid="report-date-edited" />}
      </span>
      <span className="flex-1 truncate px-1.5 text-left">{display}</span>
      <span className="tabular-nums flex-shrink-0 inline-flex items-center gap-0.5">
        {hasAmountEdits && <History className="h-2.5 w-2.5 text-amber-600" data-testid="report-amount-edited" />}
        {fmt(txn.amount)}
      </span>
    </button>
  );
}

/* ─── Level 3: Detail ─── */
function CollapsibleDetail({ detail, onEditTxn, forceOpen }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen ?? open;
  const hasTxns = detail.transactions.length > 0;
  return (
    <div data-testid="report-detail">
      <button onClick={() => hasTxns && setOpen(!open)} className={`w-full flex items-center justify-between py-0.5 px-1 -mx-1 rounded-sm ${hasTxns ? 'cursor-pointer hover:bg-[#f5f0eb]/30' : 'cursor-default'} transition-colors`} data-testid="report-detail-toggle">
        <span className="flex items-center gap-1 text-[11px] text-[#8a9bae]">
          {hasTxns ? (isOpen ? <ChevronDown className="h-2.5 w-2.5 flex-shrink-0" /> : <ChevronRight className="h-2.5 w-2.5 flex-shrink-0" />) : <span className="w-2.5" />}
          {detail.name}
        </span>
        <span className="text-[11px] tabular-nums text-[#8a9bae]">{fmt(detail.total)}</span>
      </button>
      {isOpen && hasTxns && (
        <div className="ml-4 border-l border-border/30 pl-2.5 my-0.5">
          {detail.transactions.map((txn) => (
            <TxnRow key={txn.id} txn={txn} onEdit={onEditTxn} size="sm" />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Level 2: Subcategory ─── */
function CollapsibleSubcategory({ subcategory, onEditTxn, forceOpen }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen ?? open;
  const hasChildren = (subcategory.details?.length > 0) || (subcategory.direct_transactions?.length > 0);
  return (
    <div data-testid="report-subcategory">
      <button onClick={() => hasChildren && setOpen(!open)} className={`w-full flex items-center justify-between py-1 px-1 -mx-1 rounded-sm group ${hasChildren ? 'cursor-pointer hover:bg-[#f5f0eb]/40' : 'cursor-default'} transition-colors`} data-testid="report-subcategory-toggle">
        <span className="flex items-center gap-1.5 text-[12px] text-[#5b6773]">
          {hasChildren ? (isOpen ? <ChevronDown className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" />) : <span className="w-3" />}
          {subcategory.name}
        </span>
        <span className="text-[12px] tabular-nums text-[#5b6773]" data-testid="subcategory-total">{fmt(subcategory.total)}</span>
      </button>
      {isOpen && hasChildren && (
        <div className="ml-[18px]">
          {subcategory.details?.map((d) => (
            <CollapsibleDetail key={d.name} detail={d} onEditTxn={onEditTxn} forceOpen={forceOpen} />
          ))}
          {subcategory.direct_transactions?.map((txn) => (
            <TxnRow key={txn.id} txn={txn} onEdit={onEditTxn} size="md" />
          ))}
          <div className="flex items-center justify-between py-0.5 px-1 mt-0.5 border-t border-border/30 text-[11px] italic text-[#8a9bae]" data-testid="subcategory-subtotal">
            <span>Total {subcategory.name}</span>
            <span className="tabular-nums">{fmt(subcategory.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Level 1: Category ─── */
function CollapsibleCategory({ category, onEditTxn, forceOpen, onToggleHidden, isHidden }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen ?? open;
  const hasChildren = category.subcategories.length > 0 || category.direct_transactions.length > 0;
  return (
    <div data-testid="report-category">
      <div className={`group flex items-center justify-between py-1.5 ${hasChildren ? 'hover:bg-[#f5f0eb]/60' : ''} rounded-sm px-1 -mx-1 transition-colors ${isHidden ? 'opacity-40' : ''}`}>
        <button onClick={() => hasChildren && setOpen(!open)} className="flex items-center gap-1.5 flex-1 min-w-0 text-left" data-testid="report-category-toggle">
          {hasChildren ? (isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />) : <span className="w-3.5" />}
          <span className={`text-[13px] font-semibold ${isHidden ? 'text-muted-foreground line-through' : 'text-[#1f2933]'}`}>{category.name}</span>
        </button>
        {onToggleHidden && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleHidden(category.name); }}
            className={`opacity-0 group-hover:opacity-100 focus:opacity-100 ${isHidden ? 'opacity-100' : ''} mr-2 h-6 w-6 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-sky-700 hover:bg-sky-50 transition-all print:hidden`}
            title={isHidden ? 'Show in totals' : 'Hide from totals'}
            data-testid="toggle-hide-cat-btn"
          >
            {isHidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </button>
        )}
        <span className="text-[13px] font-semibold tabular-nums text-[#1f2933]" data-testid="category-total">{fmt(category.total)}</span>
      </div>
      {isOpen && hasChildren && !isHidden && (
        <div className="ml-5">
          {category.subcategories.map((sub) => (
            <CollapsibleSubcategory key={sub.name} subcategory={sub} onEditTxn={onEditTxn} forceOpen={forceOpen} />
          ))}
          {category.direct_transactions.map((txn) => (
            <TxnRow key={txn.id} txn={txn} onEdit={onEditTxn} size="lg" />
          ))}
          <div className="flex items-center justify-between py-1 px-1 mt-1 border-t border-border/50 text-[12px] font-medium text-[#5b6773]" data-testid="category-subtotal">
            <span>Total {category.name}</span>
            <span className="tabular-nums">{fmt(category.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function TotalLine({ label, amount, bold = false, thick = false, testId = 'report-total-line' }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${thick ? 'border-t-2 border-[#1f2933]/25 pt-3 mt-1' : ''}`} data-testid={testId}>
      <span className={`text-[13px] ${bold ? 'font-bold text-[#1f2933]' : 'font-semibold text-[#5b6773]'}`}>{label}</span>
      <span className={`text-[13px] tabular-nums ${bold ? 'font-bold text-[#1f2933]' : 'font-semibold text-[#5b6773]'}`}>{fmt(amount)}</span>
    </div>
  );
}

/* Top-level collapsible section (Income / Expense / Unlabeled) */
function TopSection({ sectionKey, title, color, categories, total, collapsed, onToggle, onEditTxn, forceOpen, hiddenCats, onToggleHidden }) {
  if (categories.length === 0 && (total || 0) === 0) return null;
  const visibleTotal = categories
    .filter(c => !hiddenCats?.has(`${sectionKey}:${c.name}`))
    .reduce((s, c) => s + (c.total || 0), 0);
  const anyHidden = categories.some(c => hiddenCats?.has(`${sectionKey}:${c.name}`));
  return (
    <div className="mb-4" data-testid={`report-${sectionKey}-section`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-1.5 mb-1 hover:bg-[#f5f0eb]/60 rounded-sm px-1 -mx-1 transition-colors group"
        data-testid={`toggle-${sectionKey}-section`}
      >
        <span className="flex items-center gap-1.5">
          {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className={`text-[13px] font-bold uppercase tracking-wide ${color}`}>{title}</span>
          <Badge variant="outline" className="text-[10px] font-normal h-5 px-1.5">{categories.length}</Badge>
          {anyHidden && (
            <Badge variant="outline" className="text-[10px] font-normal h-5 px-1.5 border-sky-300 text-sky-700 bg-sky-50 gap-0.5" data-testid={`hidden-badge-${sectionKey}`}>
              <EyeOff className="h-2.5 w-2.5" />filtered
            </Badge>
          )}
        </span>
        <span className={`text-[13px] font-bold tabular-nums ${color}`}>{fmt(visibleTotal)}</span>
      </button>
      {!collapsed && (
        <>
          <div className="ml-2">
            {categories.map((cat) => (
              <CollapsibleCategory
                key={cat.name}
                category={cat}
                onEditTxn={onEditTxn}
                forceOpen={forceOpen}
                isHidden={hiddenCats?.has(`${sectionKey}:${cat.name}`)}
                onToggleHidden={onToggleHidden ? (n) => onToggleHidden(`${sectionKey}:${n}`) : undefined}
              />
            ))}
          </div>
          <div className="border-t border-[#1f2933]/15 my-1.5" />
          <TotalLine label={`Total ${title.charAt(0) + title.slice(1).toLowerCase()}`} amount={visibleTotal} bold testId={`total-${sectionKey}-line`} />
          {anyHidden && total !== visibleTotal && (
            <div className="flex items-center justify-between text-[10px] text-muted-foreground px-1">
              <span>(excl. hidden)</span>
              <span className="tabular-nums">Full: {fmt(total)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);  // 'csv' | 'pdf' | null
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  // Top-level section collapse state
  const [incomeCollapsed, setIncomeCollapsed] = useState(false);
  const [expenseCollapsed, setExpenseCollapsed] = useState(false);
  const [noneCollapsed, setNoneCollapsed] = useState(false);
  // Master-collapse for all nested categories (forceOpen true = expand all, false = collapse all, undefined = individual)
  const [forceAll, setForceAll] = useState(undefined);

  // Edit transaction from report — uses a flat list of all transactions for Prev/Next
  const [editOpen, setEditOpen] = useState(false);
  const [allTxns, setAllTxns] = useState([]);
  const [editIndex, setEditIndex] = useState(-1);
  const editTarget = editIndex >= 0 && editIndex < allTxns.length ? allTxns[editIndex] : null;

  // Excluded viewer dialog (individual excluded category drill-down)
  const [excludedViewerOpen, setExcludedViewerOpen] = useState(false);
  const [excludedViewCat, setExcludedViewCat] = useState(null);  // name of excluded category selected
  const [excludedReport, setExcludedReport] = useState(null);  // separate fetch scoped to dialog's date range
  const [excludedLoading, setExcludedLoading] = useState(false);
  const [exclFrom, setExclFrom] = useState('');
  const [exclTo, setExclTo] = useState('');
  const [exclAppliedFrom, setExclAppliedFrom] = useState('');
  const [exclAppliedTo, setExclAppliedTo] = useState('');

  // ─── Hidden categories (reflected in Saved Views) ───
  // Stored as Set<string> where string = "sectionKey:categoryName" (e.g. "income:Rent")
  const [hiddenCats, setHiddenCats] = useState(new Set());
  const toggleHiddenCat = (key) => {
    setHiddenCats(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  // ─── Saved Views (server-side) ───
  const [savedViews, setSavedViews] = useState([]);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [viewsBusy, setViewsBusy] = useState(false);

  const refreshSavedViews = useCallback(async () => {
    try {
      const data = await getReportSavedViews();
      setSavedViews(data);
    } catch { toast.error('Failed to load saved views'); }
  }, []);

  useEffect(() => {
    refreshSavedViews();
    // Migrate legacy localStorage views on first load (one-shot)
    try {
      const LEGACY = 'reports_saved_views_v1';
      const raw = localStorage.getItem(LEGACY);
      if (!raw) return;
      const legacy = JSON.parse(raw);
      if (!Array.isArray(legacy) || legacy.length === 0) { localStorage.removeItem(LEGACY); return; }
      (async () => {
        for (const v of legacy) {
          try {
            await createReportSavedView({
              name: v.name,
              date_from: v.date_from || '',
              date_to: v.date_to || '',
              income_collapsed: !!v.income_collapsed,
              expense_collapsed: !!v.expense_collapsed,
              none_collapsed: !!v.none_collapsed,
              hidden_categories: [],
            });
          } catch { /* skip failed migration */ }
        }
        localStorage.removeItem(LEGACY);
        refreshSavedViews();
        toast.success(`Migrated ${legacy.length} saved view${legacy.length === 1 ? '' : 's'} to the server`);
      })();
    } catch { /* ignore */ }
  }, [refreshSavedViews]);

  const handleSaveView = async () => {
    const name = newViewName.trim();
    if (!name) return;
    setViewsBusy(true);
    try {
      await createReportSavedView({
        name,
        date_from: appliedFrom,
        date_to: appliedTo,
        income_collapsed: incomeCollapsed,
        expense_collapsed: expenseCollapsed,
        none_collapsed: noneCollapsed,
        hidden_categories: Array.from(hiddenCats),
      });
      toast.success(`Saved view "${name}"`);
      setNewViewName('');
      setSaveViewOpen(false);
      await refreshSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save view');
    } finally { setViewsBusy(false); }
  };

  const handleLoadView = (v) => {
    setDateFrom(v.date_from || '');
    setDateTo(v.date_to || '');
    setAppliedFrom(v.date_from || '');
    setAppliedTo(v.date_to || '');
    setIncomeCollapsed(!!v.income_collapsed);
    setExpenseCollapsed(!!v.expense_collapsed);
    setNoneCollapsed(!!v.none_collapsed);
    setHiddenCats(new Set(v.hidden_categories || []));
    setForceAll(undefined);
    toast.success(`Loaded "${v.name}"`);
  };

  const handleDeleteView = async (id) => {
    try {
      await deleteReportSavedView(id);
      await refreshSavedViews();
      toast.success('View deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (appliedFrom) params.date_from = appliedFrom;
      if (appliedTo) params.date_to = appliedTo;
      const [data, cats, all] = await Promise.all([
        getTransactionReport(params),
        getTransactionCategories(),
        getBankTransactions({}),
      ]);
      setReport(data);
      setCategories(cats);
      setAllTxns(all);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [appliedFrom, appliedTo]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Fetch excluded-scoped report when dialog opens or its date range changes
  useEffect(() => {
    if (!excludedViewerOpen) return;
    let cancelled = false;
    (async () => {
      setExcludedLoading(true);
      try {
        const params = {};
        if (exclAppliedFrom) params.date_from = exclAppliedFrom;
        if (exclAppliedTo) params.date_to = exclAppliedTo;
        const data = await getTransactionReport(params);
        if (!cancelled) setExcludedReport(data);
      } catch { if (!cancelled) toast.error('Failed to load excluded data'); }
      finally { if (!cancelled) setExcludedLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [excludedViewerOpen, exclAppliedFrom, exclAppliedTo]);

  const applyDates = () => { setAppliedFrom(dateFrom); setAppliedTo(dateTo); };
  const clearDates = () => { setDateFrom(''); setDateTo(''); setAppliedFrom(''); setAppliedTo(''); };

  const handleEditTxn = (summary) => {
    const idx = allTxns.findIndex(t => t.id === summary.id);
    if (idx >= 0) {
      setEditIndex(idx);
      setEditOpen(true);
    }
  };

  const dateRangeLabel = () => {
    if (appliedFrom && appliedTo) return `${formatDate(appliedFrom)} - ${formatDate(appliedTo)}`;
    if (appliedFrom) return `From ${formatDate(appliedFrom)}`;
    if (appliedTo) return `Through ${formatDate(appliedTo)}`;
    return 'All Dates';
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = {};
      if (appliedFrom) params.date_from = appliedFrom;
      if (appliedTo) params.date_to = appliedTo;
      await downloadReportExport(format, params);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to export ${format.toUpperCase()}`);
    } finally { setExporting(null); }
  };

  const collapseAll = () => { setForceAll(false); setIncomeCollapsed(false); setExpenseCollapsed(false); setNoneCollapsed(false); };
  const expandAll = () => { setForceAll(true); setIncomeCollapsed(false); setExpenseCollapsed(false); setNoneCollapsed(false); };
  const resetCollapse = () => { setForceAll(undefined); };

  const noneCats = report?.none || [];
  // Main-page "Excluded (N)" button count uses the main date range, dialog data uses its own scoped fetch
  const excludedCatsForBadge = report?.excluded || [];
  const excludedCats = (excludedViewerOpen ? excludedReport?.excluded : report?.excluded) || [];
  const excludedTotalForFooter = (excludedViewerOpen ? excludedReport?.total_excluded : report?.total_excluded) || 0;
  const unlabeledCount = useMemo(() => categories.filter(c => (c.type || 'none') === 'none').length, [categories]);

  // Visible totals (exclude hidden categories) — for Net Income recomputation
  const netIncomeVisible = useMemo(() => {
    if (!report) return 0;
    const sumVisible = (cats, sectionKey) =>
      cats.filter(c => !hiddenCats.has(`${sectionKey}:${c.name}`))
        .reduce((s, c) => s + (c.total || 0), 0);
    const inc = sumVisible(report.income || [], 'income');
    const exp = sumVisible(report.expenses || [], 'expense');
    return Math.round((inc - exp) * 100) / 100;
  }, [report, hiddenCats]);

  const hasData = report && (report.income.length > 0 || report.expenses.length > 0 || noneCats.length > 0 || excludedCats.length > 0);

  if (loading) return <div className="py-12 text-center text-muted-foreground text-sm">Loading report...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="reports-page-title">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Profit & Loss by category type</p>
        </div>
        <div className="flex items-center gap-1.5">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="saved-views-btn">
                <Bookmark className="h-3.5 w-3.5 mr-1.5" />Saved Views
                {savedViews.length > 0 && <Badge variant="secondary" className="ml-1.5 h-4 text-[10px] px-1">{savedViews.length}</Badge>}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-2" data-testid="saved-views-popover">
              {savedViews.length === 0 ? (
                <div className="py-4 px-2 text-center text-xs text-muted-foreground">
                  No saved views yet. Save the current date range below for quick access later.
                </div>
              ) : (
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {savedViews.map(v => (
                    <div key={v.id} className="flex items-center gap-1 rounded-md hover:bg-muted/60 transition-colors" data-testid="saved-view-row">
                      <button
                        onClick={() => handleLoadView(v)}
                        className="flex-1 flex flex-col items-start px-2 py-1.5 text-left min-w-0"
                        data-testid="load-saved-view-btn"
                      >
                        <span className="text-[12px] font-semibold truncate max-w-full">{v.name}</span>
                        <span className="text-[10px] text-muted-foreground truncate max-w-full">
                          {v.date_from && v.date_to ? `${v.date_from} → ${v.date_to}` :
                            v.date_from ? `from ${v.date_from}` :
                              v.date_to ? `through ${v.date_to}` : 'All Dates'}
                          {(v.hidden_categories || []).length > 0 && (
                            <span className="ml-1 text-sky-700">· {v.hidden_categories.length} hidden</span>
                          )}
                        </span>
                      </button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => handleDeleteView(v.id)} data-testid="delete-saved-view-btn">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="pt-2 border-t border-border/40 mt-2">
                <Button size="sm" variant="outline" className="w-full h-7 text-[11px] gap-1" onClick={() => { setNewViewName(''); setSaveViewOpen(true); }} data-testid="open-save-view-btn">
                  <Save className="h-3 w-3" />Save current view
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          {excludedCatsForBadge.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => {
              // Seed dialog dates from main page's applied range
              setExclFrom(appliedFrom);
              setExclTo(appliedTo);
              setExclAppliedFrom(appliedFrom);
              setExclAppliedTo(appliedTo);
              setExcludedViewerOpen(true);
            }} data-testid="open-excluded-btn">
              <EyeOff className="h-3.5 w-3.5 mr-1.5" />Excluded ({excludedCatsForBadge.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')} disabled={exporting === 'csv'} data-testid="export-csv-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" />{exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting === 'pdf'} data-testid="export-pdf-btn">
            <FileDown className="h-3.5 w-3.5 mr-1.5" />{exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-report-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" />Print
          </Button>
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap print:hidden" data-testid="report-date-filter">
        <div className="space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">From</span>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-40 text-xs" data-testid="report-date-from" />
        </div>
        <div className="space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">To</span>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-40 text-xs" data-testid="report-date-to" />
        </div>
        <Button size="sm" className="h-8 text-xs" onClick={applyDates} data-testid="apply-dates-btn">
          <Filter className="h-3 w-3 mr-1" />Apply
        </Button>
        {(appliedFrom || appliedTo) && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearDates} data-testid="clear-dates-btn">Clear</Button>}
        <div className="flex-1" />
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={expandAll} data-testid="expand-all-btn">Expand all</Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={collapseAll} data-testid="collapse-all-btn">Collapse all</Button>
        {forceAll !== undefined && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={resetCollapse} data-testid="reset-collapse-btn">Reset</Button>
        )}
      </div>

      {hiddenCats.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-sky-200 bg-sky-50/70 text-[12px] text-sky-800 print:hidden" data-testid="hidden-cats-strip">
          <EyeOff className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            <strong>{hiddenCats.size}</strong> categor{hiddenCats.size === 1 ? 'y' : 'ies'} hidden from totals.
          </span>
          <div className="flex-1 min-w-0 text-[11px] text-sky-700/80 truncate">
            {Array.from(hiddenCats).map(k => k.split(':')[1]).join(', ')}
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-[11px] text-sky-800 hover:bg-sky-100" onClick={() => setHiddenCats(new Set())} data-testid="clear-hidden-cats-btn">
            Show all
          </Button>
        </div>
      )}

      {unlabeledCount > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50/80 text-[12px] text-amber-800 print:hidden" data-testid="unlabeled-warning">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{unlabeledCount}</strong> {unlabeledCount === 1 ? 'category is' : 'categories are'} unlabeled (type: <em>none</em>). Their transactions appear under &ldquo;Unlabeled&rdquo; below. Open <strong>Manage Categories</strong> on the Transactions page to assign Income, Expense, or Excluded.
          </span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-border/60 shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_30px_rgba(16,24,40,0.06)] overflow-hidden print:shadow-none print:border-0 print:rounded-none" data-testid="report-card">
        <div className="text-center py-6 border-b border-border/40 print:py-4">
          <h2 className="text-lg font-heading font-bold text-[#1f2933]" data-testid="report-title">Profit and Loss</h2>
          <p className="text-sm font-medium text-[#5b6773] mt-0.5">Furnished Rentals LLC</p>
          <p className="text-xs text-muted-foreground mt-1" data-testid="report-date-range">{dateRangeLabel()}</p>
        </div>

        {hasData ? (
          <div className="px-6 sm:px-10 py-4 print:px-4">
            <div className="flex justify-end pb-2 border-b border-[#1f2933]/10 mb-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Total</span>
            </div>

            <TopSection
              sectionKey="income"
              title="Income"
              color="text-emerald-700"
              categories={report.income}
              total={report.total_income}
              collapsed={incomeCollapsed}
              onToggle={() => setIncomeCollapsed(c => !c)}
              onEditTxn={handleEditTxn}
              forceOpen={forceAll}
              hiddenCats={hiddenCats}
              onToggleHidden={toggleHiddenCat}
            />

            <TopSection
              sectionKey="expense"
              title="Expenses"
              color="text-red-700"
              categories={report.expenses}
              total={report.total_expenses}
              collapsed={expenseCollapsed}
              onToggle={() => setExpenseCollapsed(c => !c)}
              onEditTxn={handleEditTxn}
              forceOpen={forceAll}
              hiddenCats={hiddenCats}
              onToggleHidden={toggleHiddenCat}
            />

            {noneCats.length > 0 && (
              <TopSection
                sectionKey="none"
                title="Unlabeled"
                color="text-amber-700"
                categories={noneCats}
                total={report.total_none}
                collapsed={noneCollapsed}
                onToggle={() => setNoneCollapsed(c => !c)}
                onEditTxn={handleEditTxn}
                forceOpen={forceAll}
                hiddenCats={hiddenCats}
                onToggleHidden={toggleHiddenCat}
              />
            )}

            <TotalLine label="Net Income" amount={netIncomeVisible} bold thick testId="net-income-line" />
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground" data-testid="report-empty-state">
            <p className="text-sm font-medium">No transactions found</p>
            <p className="text-xs mt-1">Upload transactions and assign categories to generate a report</p>
          </div>
        )}

        <div className="px-6 sm:px-10 py-3 border-t border-border/30 text-[10px] text-muted-foreground print:text-[9px]">
          Generated {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Edit transaction dialog — opens when clicking any transaction in report */}
      <TransactionEditDialog
        open={editOpen}
        onOpenChange={(v) => { setEditOpen(v); if (!v) setEditIndex(-1); }}
        transaction={editTarget}
        categories={categories}
        onSaved={() => fetchReport()}
        onPrev={() => setEditIndex(i => Math.max(0, i - 1))}
        onNext={() => setEditIndex(i => Math.min(allTxns.length - 1, i + 1))}
        hasPrev={editIndex > 0}
        hasNext={editIndex >= 0 && editIndex < allTxns.length - 1}
        position={editIndex >= 0 ? { current: editIndex + 1, total: allTxns.length } : null}
      />

      {/* Save View Dialog */}
      <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
        <DialogContent className="max-w-sm" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-4 w-4" />Save Current View
            </DialogTitle>
            <DialogDescription>
              Saves the current date range and section collapse states. Load any time from Saved Views.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              value={newViewName}
              onChange={e => setNewViewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newViewName.trim()) handleSaveView(); }}
              placeholder="e.g., Q1 2026 P&L"
              autoFocus
              data-testid="save-view-name-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Range: {(appliedFrom || appliedTo) ? `${appliedFrom || '—'} → ${appliedTo || '—'}` : 'All Dates'}
              {hiddenCats.size > 0 && <span className="text-sky-700"> · {hiddenCats.size} hidden categor{hiddenCats.size === 1 ? 'y' : 'ies'}</span>}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveViewOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveView} disabled={!newViewName.trim() || viewsBusy} data-testid="save-view-confirm-btn">{viewsBusy ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excluded Categories Viewer */}
      <Dialog open={excludedViewerOpen} onOpenChange={(v) => { setExcludedViewerOpen(v); if (!v) setExcludedViewCat(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="excluded-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EyeOff className="h-4 w-4" />
              <span>Excluded Categories</span>
              <Badge variant="outline" className="text-[11px] font-normal">{excludedCats.length}</Badge>
            </DialogTitle>
            <DialogDescription>
              Categories marked as &ldquo;Excluded&rdquo; don&apos;t appear in the main P&amp;L report. Click any category to see its transactions.
            </DialogDescription>
          </DialogHeader>

          {/* Date filter — independent of main report */}
          <div className="flex items-end gap-2 flex-wrap pb-2 border-b border-border/40" data-testid="excluded-date-filter">
            <div className="space-y-1">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">From</span>
              <Input type="date" value={exclFrom} onChange={e => setExclFrom(e.target.value)} className="h-8 w-40 text-xs" data-testid="excluded-date-from" />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">To</span>
              <Input type="date" value={exclTo} onChange={e => setExclTo(e.target.value)} className="h-8 w-40 text-xs" data-testid="excluded-date-to" />
            </div>
            <Button size="sm" className="h-8 text-xs" onClick={() => { setExclAppliedFrom(exclFrom); setExclAppliedTo(exclTo); }} data-testid="excluded-apply-dates-btn">
              <Filter className="h-3 w-3 mr-1" />Apply
            </Button>
            {(exclAppliedFrom || exclAppliedTo) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setExclFrom(''); setExclTo(''); setExclAppliedFrom(''); setExclAppliedTo(''); }} data-testid="excluded-clear-dates-btn">
                Clear
              </Button>
            )}
            <div className="flex-1" />
            <span className="text-[11px] text-muted-foreground">
              {exclAppliedFrom && exclAppliedTo ? `${formatDateShort(exclAppliedFrom)} – ${formatDateShort(exclAppliedTo)}` :
               exclAppliedFrom ? `From ${formatDateShort(exclAppliedFrom)}` :
               exclAppliedTo ? `Through ${formatDateShort(exclAppliedTo)}` : 'All Dates'}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {excludedLoading ? (
              <div className="py-12 text-center text-sm text-muted-foreground" data-testid="excluded-loading">Loading…</div>
            ) : excludedCats.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No excluded categories in this date range.</div>
            ) : !excludedViewCat ? (
              <div className="space-y-1.5">
                {excludedCats.map(cat => (
                  <button
                    key={cat.name}
                    onClick={() => setExcludedViewCat(cat.name)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-border/60 bg-white hover:bg-[#faf8f4] transition-colors text-left"
                    data-testid="excluded-cat-row"
                  >
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-[#1f2933]">{cat.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {(cat.subcategories?.length || 0) + (cat.direct_transactions?.length || 0)} items
                      </div>
                    </div>
                    <div className="text-[13px] font-bold tabular-nums text-[#5b6773]">{fmt(cat.total)}</div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-2" />
                  </button>
                ))}
                <div className="flex items-center justify-between px-3 py-2 mt-2 border-t border-border/50 text-[13px] font-bold text-[#1f2933]">
                  <span>Total Excluded</span>
                  <span className="tabular-nums">{fmt(excludedTotalForFooter)}</span>
                </div>
              </div>
            ) : (
              <div>
                <Button variant="ghost" size="sm" className="h-8 text-xs mb-3" onClick={() => setExcludedViewCat(null)} data-testid="back-to-excluded-list">
                  ← All excluded categories
                </Button>
                {(() => {
                  const cat = excludedCats.find(c => c.name === excludedViewCat);
                  if (!cat) return <div className="py-6 text-center text-sm text-muted-foreground">Category not found.</div>;
                  return (
                    <div className="bg-white rounded-xl border border-border/60 overflow-hidden">
                      <div className="text-center py-4 border-b border-border/40 bg-[#faf8f4]">
                        <h3 className="text-[15px] font-heading font-bold text-[#1f2933]" data-testid="excluded-detail-title">{cat.name}</h3>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {exclAppliedFrom && exclAppliedTo ? `${formatDate(exclAppliedFrom)} — ${formatDate(exclAppliedTo)}` :
                           exclAppliedFrom ? `From ${formatDate(exclAppliedFrom)}` :
                           exclAppliedTo ? `Through ${formatDate(exclAppliedTo)}` : 'All Dates'}
                        </p>
                      </div>
                      <div className="px-5 py-3">
                        <div className="flex justify-end pb-2 border-b border-[#1f2933]/10 mb-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total</span>
                        </div>
                        <CollapsibleCategory category={cat} onEditTxn={handleEditTxn} forceOpen={true} />
                        <TotalLine label={`Total ${cat.name}`} amount={cat.total} bold thick testId="excluded-cat-total-line" />
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setExcludedViewerOpen(false); setExcludedViewCat(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
